import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'components/reusables/modal'
import { ModalButtons } from 'components/reusables/modalButtons'
import { IRole } from 'interfaces/roles'
import { IProfile, ISimpleUser } from 'interfaces/users'
import { rolesService } from 'api/configuration/roles'
import { usersService } from 'api/configuration/users'
import { AccessLevelEnum } from 'enum/accessLevel'
import { useNotification } from 'hooks/notifications/useNotification'
import { useRolesAndPermissions } from '../../context/RolesAndPermissionsContext'
import SelectRole from './selectRole'
import SelectUsers from './selectUsers'
import SelectAccessLevel from './selectAccessLevel'

interface IProps {
  initialUsers: ISimpleUser[]
  initialRole: number
  initialAccessLevel: AccessLevelEnum
  initialProfiles: number[]
  isRolePredetermined?: boolean
  refreshView: (options?: { resetSearch?: boolean }) => Promise<void>
  close: () => void
}

const AssignRole = ({
  initialUsers,
  initialRole,
  initialAccessLevel,
  initialProfiles,
  isRolePredetermined = false,
  refreshView,
  close,
}: IProps) => {
  const { t } = useTranslation()
  const notification = useNotification()
  const prefix = 'configuration.rolesAndPermissions.assignRole'
  const { fetchRoleOptions } = useRolesAndPermissions()
  const [profiles, setProfiles] = useState<IProfile[]>([])
  const [loadingProfiles, setLoadingProfiles] = useState(false)
  const [selectedRole, setSelectedRole] = useState<IRole>()
  const [selectedUsers, setSelectedUsers] = useState<ISimpleUser[]>([])
  const [accessLevel, setAccessLevel] =
    useState<AccessLevelEnum>(initialAccessLevel)
  const [selectedProfiles, setSelectedProfiles] = useState<IProfile[]>([])
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    if (initialUsers.length > 0) {
      setSelectedUsers(initialUsers)
    }
    loadRoleOptions()
    getProfiles()
  }, [])

  const loadRoleOptions = async () => {
    const items = await fetchRoleOptions()
    if (initialRole > 0) {
      setSelectedRole(items.find((role) => role.id === initialRole))
    }
  }

  const getProfiles = async () => {
    setLoadingProfiles(true)
    await rolesService.getProfiles().then((response) => {
      setProfiles(response.data)
      if (initialProfiles.length > 0) {
        setSelectedProfiles(
          response.data.filter((profile) =>
            initialProfiles.includes(profile.id)
          )
        )
      }
    })
    setLoadingProfiles(false)
  }

  const next = async () => {
    setProcessing(true)
    const userIds = selectedUsers.map((user) => user.id)
    const profileIds = selectedProfiles.map((profile) => profile.id)
    try {
      await usersService.assignRole(
        selectedRole?.id || 0,
        userIds,
        accessLevel,
        profileIds
      )
      notification.successWithDescription(
        t(`${prefix}.notifications.success.title`),
        t(`${prefix}.notifications.success.description`)
      )
      await refreshView({ resetSearch: true })
      close()
    } finally {
      setProcessing(false)
    }
  }

  const shouldDisablePrimaryButton =
    !selectedRole ||
    selectedUsers.length === 0 ||
    (selectedProfiles.length === 0 &&
      (accessLevel === AccessLevelEnum.DirectTeamAndSpecificCollaborators ||
        accessLevel === AccessLevelEnum.SpecificCollaborators))

  return (
    <Modal
      isOpen={true}
      title={t(`${prefix}.title`)}
      maxwidth={800}
      maxheight={768}
      close={close}
      actions={
        <ModalButtons
          primaryText={t(`${prefix}.actions.assign`)}
          primaryAction={next}
          secondaryText={t(`${prefix}.actions.cancel`)}
          secondaryAction={close}
          disablePrimaryButton={shouldDisablePrimaryButton}
          processing={processing}
        />
      }
    >
      <>
        <SelectRole
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
          disabled={isRolePredetermined}
        />
        <SelectUsers
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          disabled={initialUsers.length > 0}
        />
        <SelectAccessLevel
          profiles={profiles}
          loading={loadingProfiles}
          selectedProfiles={selectedProfiles}
          setSelectedProfiles={setSelectedProfiles}
          accessLevel={accessLevel}
          setAccessLevel={setAccessLevel}
        />
      </>
    </Modal>
  )
}

export default AssignRole
